import React, { useEffect } from "react";
import { Box, Button, Text, Title } from "@mantine/core";
import { useNotifications } from "../../context/NotificationContext";
import { AlertsList } from "./AlertsList";
import styles from "./Alerts.module.css";

/**
 * Refresh the alerts and clear the red "new alert" bell each time the popover
 * or sheet opens (what visiting the old Notifications page did on mount).
 */
export function useAlertsRefresh(opened: boolean) {
  const { fetchNotifications, setIsBellRed } = useNotifications();
  useEffect(() => {
    if (!opened) return;
    fetchNotifications(1, false);
    setIsBellRed(false);
  }, [opened, fetchNotifications, setIsBellRed]);
}

/** "Alerts" title, unread count and Mark all read. */
export const AlertsHeader: React.FC = () => {
  const {
    allNotifications,
    unacknowledgedCount,
    markMultipleNotificationsAsRead,
  } = useNotifications();
  const markAllRead = () => {
    const unreadIds = allNotifications
      .filter((notification) => !notification.is_read)
      .map((notification) => notification.notification_id);
    if (unreadIds.length > 0) markMultipleNotificationsAsRead(unreadIds);
  };

  return (
    <Box className={styles.header}>
      <Box className={styles.titles}>
        <Title order={2} className={styles.title}>
          Alerts
        </Title>
        <Text className={styles.subtitle}>
          {unacknowledgedCount > 0
            ? `${unacknowledgedCount} unread`
            : "No unread alerts"}
        </Text>
      </Box>
      {unacknowledgedCount > 0 && (
        <Button
          variant="subtle"
          size="compact-sm"
          className={styles.markAllRead}
          // 14px: overrides the theme's inline 16px Button root font size.
          styles={{ root: { fontSize: 14 } }}
          onClick={markAllRead}
        >
          Mark all read
        </Button>
      )}
    </Box>
  );
};

interface AlertsPanelProps {
  onNavigate?: () => void;
}

/** Header over the scrolling list: the desktop Alerts popover's content. */
export const AlertsPanel: React.FC<AlertsPanelProps> = ({ onNavigate }) => (
  <Box className={styles.panel}>
    <Box className={styles.panelHead}>
      <AlertsHeader />
    </Box>
    <Box className={styles.scroll}>
      <AlertsList onNavigate={onNavigate} />
    </Box>
  </Box>
);
